import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useAuthStore } from "@/stores/auth";
import { api, AggregateData, SendingSourcesResponse, SendingSourceRecord, TrendData } from "@/lib/api";
import type { Domain, DMARCReport } from "@dmarc/shared";
import { useCurrentPlan } from "@/hooks/useCurrentPlan";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { reportsRoute } from "@/routeTree.gen";

// Category display mapping
function getCategoryDisplay(category: string | null): { label: string; actionNeeded: boolean } {
  switch (category) {
    case "security_gateway":
      return { label: "Recipient Security", actionNeeded: false };
    case "collaboration":
      return { label: "Email Platform", actionNeeded: true };
    case "esp":
      return { label: "Sending Service", actionNeeded: true };
    case "marketing":
      return { label: "Marketing Platform", actionNeeded: true };
    case "crm":
      return { label: "CRM", actionNeeded: true };
    case "support":
      return { label: "Support Platform", actionNeeded: true };
    case "events":
      return { label: "Events Platform", actionNeeded: true };
    case "transactional":
      return { label: "Transactional Email", actionNeeded: true };
    case "ecommerce":
      return { label: "E-commerce", actionNeeded: true };
    default:
      return { label: "Other", actionNeeded: true };
  }
}

type Status = "compliant" | "forwarded" | "non_compliant" | "threat";
const ALL_STATUSES: Status[] = ["compliant", "forwarded", "non_compliant", "threat"];


export function ReportsPage() {
  const { currentOrganization } = useAuthStore();
  const navigate = useNavigate();
  const { domain: selectedDomain, days: daysParam, status: statusParam } = reportsRoute.useSearch();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  // Determine allowed days based on plan
  const { plan, retentionDays } = useCurrentPlan();
  const allowedDays = [7, 14, 30].filter(d => d <= retentionDays);

  // Default to max allowed days, but respect URL param if within limit
  const defaultDays = allowedDays[allowedDays.length - 1] || 7;
  const days = (daysParam === 7 || daysParam === 14 || daysParam === 30)
    ? (allowedDays.includes(daysParam) ? daysParam : defaultDays)
    : defaultDays;

  // Parse status param into array of selected statuses
  const selectedStatuses: Status[] = statusParam
    ? (statusParam.split(",").filter(s => ALL_STATUSES.includes(s as Status)) as Status[])
    : ALL_STATUSES;
  const allSelected = selectedStatuses.length === ALL_STATUSES.length;

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sourcesLimit] = useState(50);
  const [sourcesOffset, setSourcesOffset] = useState(0);
  const [reportsLimit] = useState(50);
  const [reportsOffset, setReportsOffset] = useState(0);

  const { data: domains } = useQuery<Domain[]>({
    queryKey: ["domains", currentOrganization?.id],
    queryFn: () => api.domains.list(currentOrganization!.id),
    enabled: !!currentOrganization,
  });

  const { data: aggregate, isLoading: aggregateLoading } = useQuery<AggregateData>({
    queryKey: ["reports-aggregate", currentOrganization?.id, selectedDomain, days],
    queryFn: () =>
      api.reports.aggregate({
        organizationId: currentOrganization!.id,
        domainId: selectedDomain,
        days,
      }),
    enabled: !!currentOrganization,
  });

  const { data: trends } = useQuery<TrendData[]>({
    queryKey: ["reports-trends", currentOrganization?.id, selectedDomain, days],
    queryFn: () =>
      api.reports.trends({
        organizationId: currentOrganization!.id,
        domainId: selectedDomain,
        days,
      }),
    enabled: !!currentOrganization,
  });

  const statusParam_api = allSelected ? "all" : selectedStatuses.join(",");
  const { data: sendingSources, isLoading: sourcesLoading } = useQuery<SendingSourcesResponse>({
    queryKey: ["sending-sources", currentOrganization?.id, selectedDomain, days, statusParam_api, sourcesLimit, sourcesOffset],
    queryFn: () =>
      api.reports.sendingSources({
        organizationId: currentOrganization!.id,
        domainId: selectedDomain,
        days,
        status: statusParam_api,
        limit: sourcesLimit,
        offset: sourcesOffset,
      }),
    enabled: !!currentOrganization,
  });

  const { data: reports, isLoading: reportsLoading } = useQuery<DMARCReport[]>({
    queryKey: ["reports", currentOrganization?.id, selectedDomain, reportsLimit, reportsOffset],
    queryFn: () =>
      api.reports.list({
        organizationId: currentOrganization!.id,
        domainId: selectedDomain,
        limit: reportsLimit,
        offset: reportsOffset,
      }),
    enabled: !!currentOrganization,
  });

  if (!currentOrganization) {
    return (
      <div className="text-center text-muted-foreground">
        No organization selected
      </div>
    );
  }

  const handleExportCsv = async () => {
    try {
      const blob = await api.reports.exportCsv({
        organizationId: currentOrganization.id,
        domainId: selectedDomain,
        days,
        type: "aggregate",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dmarc-aggregate-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const toggleStatus = (status: Status) => {
    let newStatuses: Status[];
    if (selectedStatuses.includes(status)) {
      // Remove it (but don't allow empty selection)
      newStatuses = selectedStatuses.filter(s => s !== status);
      if (newStatuses.length === 0) {
        // If removing would leave empty, select only the others
        newStatuses = ALL_STATUSES.filter(s => s !== status);
      }
    } else {
      // Add it
      newStatuses = [...selectedStatuses, status];
    }

    // Sort to maintain consistent order
    newStatuses.sort((a, b) => ALL_STATUSES.indexOf(a) - ALL_STATUSES.indexOf(b));

    navigate({
      to: "/reports",
      search: {
        domain: selectedDomain,
        days,
        status: newStatuses.length === ALL_STATUSES.length ? undefined : newStatuses.join(","),
      }
    });
    setSourcesOffset(0);
  };

  const clearFilters = () => {
    navigate({
      to: "/reports",
      search: {
        domain: selectedDomain,
        days,
        status: undefined,
      }
    });
    setSourcesOffset(0);
  };

  const formatDateRange = (begin: string, end: string) => {
    const formatDate = (dateStr: string) => {
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };
    const beginDate = new Date(begin);
    const endDate = new Date(end);
    const sameDay = beginDate.toDateString() === endDate.toDateString();

    if (sameDay) {
      return formatDate(begin);
    }

    return `${formatDate(begin)} - ${formatDate(end)}`;
  };

  const getPassRate = (report: DMARCReport) => {
    if (report.totalCount === 0) return 0;
    return Math.round((report.passCount / report.totalCount) * 100);
  };

  const formattedTrends = trends?.map((t) => ({
    date: new Date(t.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    compliant: t.compliant,
    forwarded: t.forwarded,
    nonCompliant: t.nonCompliant,
    threat: t.threat,
    total: t.total,
  }));

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="page-header page-header-primary">
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="page-header-icon">
              <svg className="icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold">DMARC Reports</h1>
              <p className="text-white/80">Email authentication status and sending sources</p>
            </div>
          </div>
          {/* Time range filter */}
          <div className="flex rounded-lg bg-white/20 p-1 backdrop-blur-sm border border-white/30">
            {[7, 14, 30].map((d) => {
              const isAllowed = allowedDays.includes(d);
              return (
                <button
                  key={d}
                  onClick={() => {
                    if (isAllowed) {
                      navigate({ to: "/reports", search: { domain: selectedDomain, days: d, status: allSelected ? undefined : selectedStatuses.join(",") } });
                    } else {
                      setShowUpgradePrompt(true);
                    }
                  }}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                    days === d
                      ? "bg-white text-indigo-600 shadow-sm"
                      : isAllowed
                        ? "text-white hover:bg-white/20"
                        : "text-white/50 hover:text-white hover:bg-white/10 cursor-pointer"
                  }`}
                  title={!isAllowed ? "Click to upgrade" : undefined}
                >
                  {d}d
                  {!isAllowed && (
                    <svg className="inline-block ml-1 w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <div className="page-header-decor" />
        <div className="page-header-decor-alt" />
      </div>

      {/* Filters Row */}
      <div className="card">
        <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
          <div className="flex-1 min-w-0 sm:min-w-[200px]">
            <label className="block text-sm font-medium mb-2">Filter by Domain</label>
            <select
              value={selectedDomain || ""}
              onChange={(e) => {
                navigate({ to: "/reports", search: { domain: e.target.value || undefined, days, status: allSelected ? undefined : selectedStatuses.join(",") } });
                setSourcesOffset(0);
                setReportsOffset(0);
              }}
              className="input input-primary w-full"
            >
              <option value="">All domains</option>
              {domains?.map((domain) => (
                <option key={domain.id} value={domain.id}>
                  {domain.domain}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-2 sm:pt-6">
            <button onClick={handleExportCsv} className="btn btn-secondary btn-sm sm:btn-md">
              <svg className="h-4 w-4 sm:mr-2" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              <span className="hidden sm:inline">Export CSV</span>
              <span className="sm:hidden">CSV</span>
            </button>
          </div>
        </div>
      </div>

      {/* Clickable Stats Cards - Multi-select */}
      {aggregateLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="stat-card animate-pulse">
              <div className="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
              <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          ))}
        </div>
      ) : aggregate?.summary ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
          <button
            onClick={() => toggleStatus("compliant")}
            className={`stat-card border-l-4 border-l-green-500 text-left transition-all ${
              selectedStatuses.includes("compliant")
                ? "ring-2 ring-green-500 ring-offset-2"
                : "opacity-50 hover:opacity-75"
            }`}
          >
            <div className="stat-card-content">
              <div className="stat-icon stat-icon-emerald">
                <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <div className="stat-label">Compliant</div>
                <div className="stat-value text-green-600">{aggregate.summary.compliant.toLocaleString()}</div>
                {aggregate.summary.total > 0 && (
                  <div className="text-sm text-muted-foreground">
                    {Math.round((aggregate.summary.compliant / aggregate.summary.total) * 100)}%
                  </div>
                )}
              </div>
            </div>
          </button>

          <button
            onClick={() => toggleStatus("forwarded")}
            className={`stat-card border-l-4 border-l-cyan-500 text-left transition-all ${
              selectedStatuses.includes("forwarded")
                ? "ring-2 ring-cyan-500 ring-offset-2"
                : "opacity-50 hover:opacity-75"
            }`}
          >
            <div className="stat-card-content">
              <div className="stat-icon stat-icon-cyan">
                <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                </svg>
              </div>
              <div>
                <div className="stat-label">Forwarded</div>
                <div className="stat-value text-cyan-600">{aggregate.summary.forwarded.toLocaleString()}</div>
                {aggregate.summary.total > 0 && (
                  <div className="text-sm text-muted-foreground">
                    {Math.round((aggregate.summary.forwarded / aggregate.summary.total) * 100)}%
                  </div>
                )}
              </div>
            </div>
          </button>

          <button
            onClick={() => toggleStatus("non_compliant")}
            className={`stat-card border-l-4 border-l-amber-500 text-left transition-all ${
              selectedStatuses.includes("non_compliant")
                ? "ring-2 ring-amber-500 ring-offset-2"
                : "opacity-50 hover:opacity-75"
            }`}
          >
            <div className="stat-card-content">
              <div className="stat-icon stat-icon-amber">
                <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </div>
              <div>
                <div className="stat-label">Non-compliant</div>
                <div className="stat-value text-amber-600">{aggregate.summary.nonCompliant.toLocaleString()}</div>
                {aggregate.summary.total > 0 && (
                  <div className="text-sm text-muted-foreground">
                    {Math.round((aggregate.summary.nonCompliant / aggregate.summary.total) * 100)}%
                  </div>
                )}
              </div>
            </div>
          </button>

          <button
            onClick={() => toggleStatus("threat")}
            className={`stat-card border-l-4 border-l-red-500 text-left transition-all ${
              selectedStatuses.includes("threat")
                ? "ring-2 ring-red-500 ring-offset-2"
                : "opacity-50 hover:opacity-75"
            }`}
          >
            <div className="stat-card-content">
              <div className="stat-icon stat-icon-rose">
                <svg className="icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0-10.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.249-8.25-3.286zm0 13.036h.008v.008H12v-.008z" />
                </svg>
              </div>
              <div>
                <div className="stat-label">Threat/Unknown</div>
                <div className="stat-value text-red-600">{aggregate.summary.threat.toLocaleString()}</div>
                {aggregate.summary.total > 0 && (
                  <div className="text-sm text-muted-foreground">
                    {Math.round((aggregate.summary.threat / aggregate.summary.total) * 100)}%
                  </div>
                )}
              </div>
            </div>
          </button>
        </div>
      ) : null}

      {/* Email Volume Trends */}
      <div className="card">
        <h3 className="text-lg font-semibold">Email Volume Trends</h3>
        <p className="text-sm text-muted-foreground">
          Daily email authentication results
          {!allSelected && (
            <span className="ml-1">
              (showing: {selectedStatuses.map(s => s === "compliant" ? "compliant" : s === "forwarded" ? "forwarded" : s === "non_compliant" ? "non-compliant" : "threat").join(", ")})
            </span>
          )}
        </p>
        <div className="mt-4 h-80">
          {formattedTrends && formattedTrends.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={formattedTrends}>
                <defs>
                  <linearGradient id="colorCompliant" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorForwarded" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorNonCompliant" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorThreat" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" fontSize={12} stroke="#9ca3af" />
                <YAxis fontSize={12} stroke="#9ca3af" />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload || payload.length === 0) return null;
                    const total = payload.reduce((sum, entry) => sum + (entry.value as number || 0), 0);
                    return (
                      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 min-w-[140px]">
                        <div className="font-medium text-sm mb-2 pb-2 border-b border-gray-200 dark:border-gray-700">{label}</div>
                        <div className="space-y-1.5">
                          {payload.map((entry, index) => (
                            <div key={index} className="flex items-center justify-between gap-4 text-sm">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: entry.color }} />
                                <span className="text-muted-foreground">{entry.name}</span>
                              </div>
                              <span className="font-medium">{(entry.value as number).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center justify-between gap-4 text-sm font-semibold mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                          <span>Total</span>
                          <span>{total.toLocaleString()}</span>
                        </div>
                      </div>
                    );
                  }}
                />
                {selectedStatuses.includes("compliant") && (
                  <Area
                    type="monotone"
                    dataKey="compliant"
                    stroke="#22c55e"
                    fillOpacity={1}
                    fill="url(#colorCompliant)"
                    name="Compliant"
                    strokeWidth={2}
                    stackId="1"
                  />
                )}
                {selectedStatuses.includes("forwarded") && (
                  <Area
                    type="monotone"
                    dataKey="forwarded"
                    stroke="#06b6d4"
                    fillOpacity={1}
                    fill="url(#colorForwarded)"
                    name="Forwarded"
                    strokeWidth={2}
                    stackId="1"
                  />
                )}
                {selectedStatuses.includes("non_compliant") && (
                  <Area
                    type="monotone"
                    dataKey="nonCompliant"
                    stroke="#f59e0b"
                    fillOpacity={1}
                    fill="url(#colorNonCompliant)"
                    name="Non-compliant"
                    strokeWidth={2}
                    stackId="1"
                  />
                )}
                {selectedStatuses.includes("threat") && (
                  <Area
                    type="monotone"
                    dataKey="threat"
                    stroke="#ef4444"
                    fillOpacity={1}
                    fill="url(#colorThreat)"
                    name="Threat"
                    strokeWidth={2}
                    stackId="1"
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              No trend data yet
            </div>
          )}
        </div>
      </div>

      {/* Sending Domains Table */}
      {aggregate?.sendingDomains && aggregate.sendingDomains.length > 0 && (
        <div className="card p-0 overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-semibold">Sending Domains</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr className="table-header">
                  <th className="table-header-cell">Domain</th>
                  <th className="table-header-cell text-right">Volume</th>
                  <th className="table-header-cell text-right">SPF Pass</th>
                  <th className="table-header-cell text-right">DKIM Pass</th>
                </tr>
              </thead>
              <tbody>
                {aggregate.sendingDomains.map((domain) => (
                  <tr key={domain.domain} className="table-row">
                    <td className="table-cell font-mono">{domain.domain}</td>
                    <td className="table-cell text-right font-medium">{domain.volume.toLocaleString()}</td>
                    <td className="table-cell text-right">
                      <span className={`badge ${domain.spfPassRate >= 90 ? "badge-success" : domain.spfPassRate >= 70 ? "badge-warning" : "badge-error"}`}>
                        {domain.spfPassRate}%
                      </span>
                    </td>
                    <td className="table-cell text-right">
                      <span className={`badge ${domain.dkimPassRate >= 90 ? "badge-success" : domain.dkimPassRate >= 70 ? "badge-warning" : "badge-error"}`}>
                        {domain.dkimPassRate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Unified Sending Sources Table with Expandable Rows */}
      <div className="card p-0 overflow-hidden">
        <div className="px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">Sending Sources</h3>
            <p className="text-sm text-muted-foreground">
              {sendingSources?.total || 0} records
              {!allSelected && (
                <span className="ml-2">
                  (filtered: {selectedStatuses.map(s => s === "compliant" ? "Compliant" : s === "forwarded" ? "Forwarded" : s === "non_compliant" ? "Non-compliant" : "Threat").join(", ")})
                </span>
              )}
            </p>
          </div>
          {!allSelected && (
            <button
              onClick={clearFilters}
              className="btn btn-secondary btn-sm"
            >
              Show All
            </button>
          )}
        </div>

        {sourcesLoading ? (
          <div className="flex items-center justify-center py-12" role="status" aria-label="Loading sources">
            <svg className="animate-spin h-8 w-8 text-indigo-500" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : sendingSources && sendingSources.records.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr className="table-header">
                    <th className="table-header-cell">Source</th>
                    <th className="table-header-cell">Domain</th>
                    <th className="table-header-cell text-right">Messages</th>
                    <th className="table-header-cell">SPF</th>
                    <th className="table-header-cell">DKIM</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Type</th>
                    <th className="table-header-cell">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sendingSources.records.map((record: SendingSourceRecord) => {
                    const isExpanded = expandedId === record.id;
                    const categoryDisplay = getCategoryDisplay(record.serviceCategory);

                    return (
                      <>
                        <tr
                          key={record.id}
                          className="table-row cursor-pointer hover:bg-muted/50"
                          onClick={() => setExpandedId(isExpanded ? null : record.id)}
                        >
                          <td className="table-cell">
                            <div className="flex items-center gap-2">
                              <svg
                                className={`icon-sm text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2}
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                              </svg>
                              <div>
                                <div className="font-medium">
                                  {record.serviceName || record.sourceIp}
                                </div>
                                {record.serviceName && (
                                  <div className="text-xs text-muted-foreground font-mono">{record.sourceIp}</div>
                                )}
                                {record.asnOrg && !record.serviceName && (
                                  <div className="text-xs text-muted-foreground">{record.asnOrg}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="table-cell font-mono text-sm">
                            {record.domain}
                          </td>
                          <td className="table-cell text-right font-medium">
                            {record.messageCount.toLocaleString()}
                          </td>
                          <td className="table-cell">
                            <div className="flex flex-col gap-1">
                              <span className={`badge ${record.spfResult === 'pass' ? 'badge-success' : 'badge-error'}`}>
                                {record.spfResult || 'none'}
                              </span>
                              {record.spfResult === 'pass' && !record.spfAligned && (
                                <span className="text-xs text-amber-600">not aligned</span>
                              )}
                            </div>
                          </td>
                          <td className="table-cell">
                            <div className="flex flex-col gap-1">
                              <span className={`badge ${record.dkimResult === 'pass' ? 'badge-success' : 'badge-error'}`}>
                                {record.dkimResult || 'none'}
                              </span>
                              {record.dkimResult === 'pass' && !record.dkimAligned && (
                                <span className="text-xs text-amber-600">not aligned</span>
                              )}
                            </div>
                          </td>
                          <td className="table-cell">
                            <span className={`badge ${
                              record.status === 'compliant' ? 'badge-success' :
                              record.status === 'forwarded' ? 'badge-info' :
                              record.status === 'non_compliant' ? 'badge-warning' : 'badge-error'
                            }`}>
                              {record.status === 'compliant' ? 'Compliant' :
                               record.status === 'forwarded' ? 'Forwarded' :
                               record.status === 'non_compliant' ? 'Non-compliant' : 'Threat'}
                            </span>
                          </td>
                          <td className="table-cell text-sm text-muted-foreground">
                            {categoryDisplay.label}
                          </td>
                          <td className="table-cell">
                            {record.status === 'compliant' ? (
                              <span className="badge badge-success">Passing</span>
                            ) : record.status === 'forwarded' ? (
                              <span className="badge badge-info">No Action</span>
                            ) : categoryDisplay.actionNeeded ? (
                              <span className="badge badge-warning">Configure</span>
                            ) : (
                              <span className="badge badge-info">No Action</span>
                            )}
                          </td>
                        </tr>

                        {/* Expanded recommendation row */}
                        {isExpanded && (
                          <tr key={`${record.id}-expanded`} className={
                            record.status === 'compliant' ? "bg-green-50 dark:bg-green-950/20" :
                            record.status === 'forwarded' ? "bg-cyan-50 dark:bg-cyan-950/20" :
                            record.serviceCategory === "security_gateway" ? "bg-blue-50 dark:bg-blue-950/20" :
                            "bg-amber-50 dark:bg-amber-950/20"
                          }>
                            <td colSpan={8} className="p-4">
                              <div className="flex items-start gap-4">
                                <div className={`p-2 rounded-lg ${
                                  record.status === 'compliant' ? "bg-green-100 dark:bg-green-900/30" :
                                  record.status === 'forwarded' ? "bg-cyan-100 dark:bg-cyan-900/30" :
                                  record.serviceCategory === "security_gateway" ? "bg-blue-100 dark:bg-blue-900/30" :
                                  "bg-amber-100 dark:bg-amber-900/30"
                                }`}>
                                  {record.status === 'compliant' ? (
                                    <svg className="icon-md text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  ) : record.status === 'forwarded' ? (
                                    <svg className="icon-md text-cyan-600 dark:text-cyan-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                                    </svg>
                                  ) : record.serviceCategory === "security_gateway" ? (
                                    <svg className="icon-md text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                                    </svg>
                                  ) : (
                                    <svg className="icon-md text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                                    </svg>
                                  )}
                                </div>
                                <div className="flex-1 space-y-3">
                                  {/* Compliant record */}
                                  {record.status === 'compliant' && (
                                    <div className="p-3 rounded-lg bg-white dark:bg-gray-900 border">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="badge badge-success">Passing DMARC</span>
                                        {record.serviceName && <span className="font-medium">{record.serviceName}</span>}
                                      </div>
                                      <p className="text-sm text-muted-foreground">
                                        This source is properly authenticated.
                                        {record.spfAligned && record.dkimAligned && " Both SPF and DKIM are aligned."}
                                        {record.spfAligned && !record.dkimAligned && " SPF is aligned."}
                                        {!record.spfAligned && record.dkimAligned && " DKIM is aligned."}
                                      </p>
                                    </div>
                                  )}

                                  {/* Forwarded email */}
                                  {record.status === 'forwarded' && (
                                    <>
                                      <div className="flex items-center gap-2">
                                        <span className="badge badge-info">No Action Needed</span>
                                        <span className="font-medium">Forwarded Email</span>
                                      </div>
                                      <div className="p-3 rounded-lg bg-white dark:bg-gray-900 border">
                                        <p className="text-sm text-muted-foreground mb-2">
                                          This email was legitimately sent by your domain (DKIM signature verified), but was then forwarded by the recipient to another address. When emails are forwarded, SPF checks pass for the forwarder's domain instead of yours, which breaks DMARC alignment.
                                        </p>
                                        <div className="text-xs space-y-1 text-cyan-600 dark:text-cyan-400 mb-3">
                                          {record.dkimDomain && (
                                            <div>DKIM signed by: <code className="px-1 py-0.5 bg-cyan-100 dark:bg-cyan-900/50 rounded">{record.dkimDomain}</code> (proves legitimacy)</div>
                                          )}
                                          {record.spfDomain && (
                                            <div>SPF passed for: <code className="px-1 py-0.5 bg-cyan-100 dark:bg-cyan-900/50 rounded">{record.spfDomain}</code> (forwarder)</div>
                                          )}
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                          <strong>This is normal behavior.</strong> Your email was sent correctly — the forwarding happened on the recipient's end. No action is needed on your part.
                                        </p>
                                      </div>
                                    </>
                                  )}

                                  {/* Alignment explanation for non-compliant (not forwarded) */}
                                  {record.status !== 'compliant' && record.status !== 'forwarded' && (record.spfResult === 'pass' || record.dkimResult === 'pass') && (!record.spfAligned && !record.dkimAligned) && (
                                    <div className="p-3 rounded-lg bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800">
                                      <div className="font-medium text-amber-800 dark:text-amber-200 mb-2">Why DMARC Failed: Authentication Not Aligned</div>
                                      <p className="text-sm text-amber-700 dark:text-amber-300 mb-2">
                                        SPF and/or DKIM passed, but for a different domain than <code className="px-1 py-0.5 bg-amber-200 dark:bg-amber-800 rounded">{record.policyDomain}</code>
                                      </p>
                                      <div className="text-xs space-y-1 text-amber-600 dark:text-amber-400">
                                        {record.spfResult === 'pass' && record.spfDomain && (
                                          <div>SPF passed for: <code className="px-1 py-0.5 bg-amber-200 dark:bg-amber-800 rounded">{record.spfDomain}</code> {record.spfAligned ? '(aligned)' : '(not aligned)'}</div>
                                        )}
                                        {record.dkimResult === 'pass' && record.dkimDomain && (
                                          <div>DKIM signed by: <code className="px-1 py-0.5 bg-amber-200 dark:bg-amber-800 rounded">{record.dkimDomain}</code> {record.dkimAligned ? '(aligned)' : '(not aligned)'}</div>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Security gateway - no action needed */}
                                  {record.status !== 'compliant' && record.status !== 'forwarded' && record.serviceCategory === "security_gateway" && (
                                    <>
                                      <div className="flex items-center gap-2">
                                        <span className="badge badge-info">No Action Needed</span>
                                        <span className="font-medium">{record.serviceName}</span>
                                      </div>
                                      <div className="p-3 rounded-lg bg-white dark:bg-gray-900 border">
                                        <p className="text-sm text-muted-foreground">
                                          <strong>{record.serviceName}</strong> is an email security tool used by the people receiving your emails. When they scan messages, it breaks authentication signatures. This is normal — your email was sent correctly, and DMARC flagged the relay as expected. No action needed on your end.
                                        </p>
                                        <Link
                                          to="/guides/recipient-security-gateways"
                                          onClick={(e) => e.stopPropagation()}
                                          className="inline-flex items-center gap-1 mt-3 text-sm text-blue-600 hover:text-blue-500"
                                        >
                                          Learn more about recipient security gateways
                                          <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                          </svg>
                                        </Link>
                                      </div>
                                    </>
                                  )}

                                  {/* Known service needing configuration */}
                                  {record.status !== 'compliant' && record.status !== 'forwarded' && record.serviceCategory !== "security_gateway" && record.serviceName && (
                                    <>
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">Identified as:</span>
                                        <span className="badge badge-primary">{record.serviceName}</span>
                                      </div>
                                      {(record.spfInclude || record.dkimSetupUrl) && (
                                        <div className="p-3 rounded-lg bg-white dark:bg-gray-900 border">
                                          <div className="flex items-center gap-2 mb-2">
                                            <svg className="icon-sm text-indigo-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                                            </svg>
                                            <span className="font-medium text-sm">How to Fix Alignment</span>
                                          </div>

                                          {record.alignmentFix === "both" && (
                                            <p className="text-sm text-muted-foreground mb-3">
                                              Either option below will fix alignment:
                                            </p>
                                          )}

                                          {record.dkimSetupUrl && (record.alignmentFix === "dkim" || record.alignmentFix === "both") && (
                                            <div className="mb-3">
                                              <p className="text-sm text-muted-foreground mb-2">
                                                Set up custom DKIM signing so {record.serviceName} signs emails with your domain:
                                              </p>
                                              <a
                                                href={record.dkimSetupUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-500"
                                              >
                                                View {record.serviceName} DKIM setup guide
                                                <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                                </svg>
                                              </a>
                                            </div>
                                          )}

                                          {record.spfInclude && (record.alignmentFix === "spf" || record.alignmentFix === "both") && (
                                            <div>
                                              <p className="text-sm text-muted-foreground mb-2">
                                                {record.alignmentFix === "both" ? 'Or add' : 'Add'} to your SPF record:
                                              </p>
                                              <div className="flex items-center gap-2">
                                                <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono">
                                                  include:{record.spfInclude}
                                                </code>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigator.clipboard.writeText(`include:${record.spfInclude}`);
                                                  }}
                                                  className="btn btn-sm btn-secondary"
                                                >
                                                  Copy
                                                </button>
                                              </div>
                                            </div>
                                          )}

                                          {/* Show SPF as supplementary info when DKIM is the only alignment fix */}
                                          {record.spfInclude && record.alignmentFix === "dkim" && (
                                            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                              <p className="text-sm text-muted-foreground mb-2">
                                                Optional: Add their SPF record to authorize their IPs (doesn't fix alignment):
                                              </p>
                                              <div className="flex items-center gap-2">
                                                <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono text-gray-500">
                                                  include:{record.spfInclude}
                                                </code>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigator.clipboard.writeText(`include:${record.spfInclude}`);
                                                  }}
                                                  className="btn btn-sm btn-secondary"
                                                >
                                                  Copy
                                                </button>
                                              </div>
                                            </div>
                                          )}

                                          {/* Fallback for services without alignmentFix data */}
                                          {!record.alignmentFix && record.dkimSetupUrl && (
                                            <div className="mb-3">
                                              <p className="text-sm text-muted-foreground mb-2">
                                                Set up domain authentication with {record.serviceName}:
                                              </p>
                                              <a
                                                href={record.dkimSetupUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-500"
                                              >
                                                View {record.serviceName} setup guide
                                                <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                                </svg>
                                              </a>
                                            </div>
                                          )}
                                          {!record.alignmentFix && !record.dkimSetupUrl && record.spfInclude && (
                                            <div>
                                              <p className="text-sm text-muted-foreground mb-2">
                                                Add to your SPF record:
                                              </p>
                                              <div className="flex items-center gap-2">
                                                <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono">
                                                  include:{record.spfInclude}
                                                </code>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigator.clipboard.writeText(`include:${record.spfInclude}`);
                                                  }}
                                                  className="btn btn-sm btn-secondary"
                                                >
                                                  Copy
                                                </button>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </>
                                  )}

                                  {/* Unknown source with ASN */}
                                  {record.status !== 'compliant' && record.status !== 'forwarded' && record.serviceCategory !== "security_gateway" && !record.serviceName && record.asnOrg && (
                                    <>
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">Hosted on:</span>
                                        <span className="text-muted-foreground">{record.asnOrg}</span>
                                      </div>
                                      <div className="p-3 rounded-lg bg-white dark:bg-gray-900 border">
                                        <p className="text-sm text-muted-foreground">
                                          This IP is hosted on {record.asnOrg} but we couldn't identify a specific email service.
                                          If you use a service from this provider, you may need to add their SPF record manually.
                                        </p>
                                      </div>
                                    </>
                                  )}

                                  {/* Complete unknown - threat */}
                                  {record.status === 'threat' && !record.serviceName && !record.asnOrg && (
                                    <div className="text-sm text-muted-foreground">
                                      <p className="font-medium mb-2">Unknown sender</p>
                                      <p>This could be:</p>
                                      <ul className="list-disc list-inside mt-2 space-y-1">
                                        <li>A legitimate service you use that we haven't identified yet</li>
                                        <li>An unauthorized sender attempting to spoof your domain</li>
                                      </ul>
                                      <p className="mt-2">
                                        Your DMARC policy ({record.disposition}) is being applied to these messages.
                                        If this is legitimate traffic, investigate the source. If it's spoofing, no action is needed.
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {sendingSources.total > sourcesLimit && (
              <div className="flex items-center justify-between border-t px-6 py-4">
                <button
                  onClick={() => setSourcesOffset(Math.max(0, sourcesOffset - sourcesLimit))}
                  disabled={sourcesOffset === 0}
                  className="btn btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-muted-foreground">
                  Showing {sourcesOffset + 1} - {Math.min(sourcesOffset + sourcesLimit, sendingSources.total)} of {sendingSources.total}
                </span>
                <button
                  onClick={() => setSourcesOffset(sourcesOffset + sourcesLimit)}
                  disabled={sourcesOffset + sourcesLimit >= sendingSources.total}
                  className="btn btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-green-500/50" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium">No records found</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">
              {!allSelected
                ? `No ${selectedStatuses.map(s => s === "compliant" ? "compliant" : s === "forwarded" ? "forwarded" : s === "non_compliant" ? "non-compliant" : "threat").join(" or ")} records in the selected time period.`
                : "No sending source records in the selected time period."}
            </p>
            {!allSelected && (
              <button onClick={clearFilters} className="btn btn-secondary mt-6">
                View All Records
              </button>
            )}
          </div>
        )}
      </div>

      {/* Upgrade Prompt Modal */}
      {showUpgradePrompt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
                <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold">Upgrade for More Data</h3>
            </div>
            <p className="text-muted-foreground mb-4">
              The free plan retains data for 7 days. Upgrade to access up to 1 year of historical data:
            </p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-center gap-2 text-sm">
                <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                <span><strong>Starter:</strong> 90 days of data retention</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                <span><strong>Pro & above:</strong> 365 days of data retention</span>
              </li>
            </ul>
            <div className="flex gap-3">
              <button
                onClick={() => setShowUpgradePrompt(false)}
                className="btn btn-secondary flex-1"
              >
                Maybe Later
              </button>
              <Link
                to="/pricing"
                className="btn btn-primary flex-1 text-center"
                onClick={() => setShowUpgradePrompt(false)}
              >
                View Plans
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Reports List */}
      <div className="card p-0 overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Individual Reports</h3>
          <p className="text-sm text-muted-foreground">{reports?.length || 0} reports</p>
        </div>
        {reportsLoading ? (
          <div className="flex items-center justify-center py-12" role="status" aria-label="Loading reports">
            <svg className="animate-spin h-8 w-8 text-indigo-500" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : reports && reports.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr className="table-header">
                    <th className="table-header-cell">Reporter</th>
                    <th className="table-header-cell">Domain</th>
                    <th className="table-header-cell">Date Range</th>
                    <th className="table-header-cell text-right">Messages</th>
                    <th className="table-header-cell text-right">Passed</th>
                    <th className="table-header-cell text-right">Failed</th>
                    <th className="table-header-cell text-right">Pass Rate</th>
                    <th className="table-header-cell">Policy</th>
                    <th className="table-header-cell"></th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => {
                    const passRate = getPassRate(report);
                    return (
                      <tr key={report.id} className="table-row">
                        <td className="table-cell">
                          <div className="font-medium">{report.orgName}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {report.reportId}
                          </div>
                        </td>
                        <td className="table-cell font-mono text-sm">
                          {report.policyDomain}
                        </td>
                        <td className="table-cell text-sm text-muted-foreground">
                          {formatDateRange(report.dateRangeBegin, report.dateRangeEnd)}
                        </td>
                        <td className="table-cell text-right font-medium">
                          {report.totalCount.toLocaleString()}
                        </td>
                        <td className="table-cell text-right font-medium text-success">
                          {report.passCount.toLocaleString()}
                        </td>
                        <td className="table-cell text-right font-medium text-error">
                          {report.failCount.toLocaleString()}
                        </td>
                        <td className="table-cell text-right">
                          <span
                            className={`badge ${
                              passRate >= 90
                                ? "badge-success"
                                : passRate >= 70
                                ? "badge-warning"
                                : "badge-error"
                            }`}
                          >
                            {passRate}%
                          </span>
                        </td>
                        <td className="table-cell">
                          <span className={`badge ${
                            report.policyP === "reject"
                              ? "badge-success"
                              : report.policyP === "quarantine"
                              ? "badge-warning"
                              : "badge-error"
                          }`}>
                            {report.policyP}
                          </span>
                        </td>
                        <td className="table-cell">
                          <Link
                            to="/reports/$reportId"
                            params={{ reportId: report.id }}
                            className="text-indigo-600 hover:text-indigo-500 text-sm font-medium"
                          >
                            View →
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {reports.length >= reportsLimit && (
              <div className="flex items-center justify-between border-t px-6 py-4">
                <button
                  onClick={() => setReportsOffset(Math.max(0, reportsOffset - reportsLimit))}
                  disabled={reportsOffset === 0}
                  className="btn btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-muted-foreground">
                  Showing {reportsOffset + 1} - {reportsOffset + reports.length}
                </span>
                <button
                  onClick={() => setReportsOffset(reportsOffset + reportsLimit)}
                  disabled={reports.length < reportsLimit}
                  className="btn btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium">No reports yet</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">
              DMARC reports are sent by email providers. Once you configure your DMARC record, reports will appear here within 24-48 hours.
            </p>
            <Link to="/domains" className="btn btn-primary mt-6">
              Configure Domains
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
